import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Search, 
  Filter, 
  Grid, 
  List, 
  FolderOpen,
  Folder,
  Eye, 
  Download, 
  Calendar,
  BookOpen,
  Book,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Home,
  ArrowLeft,
  MoreVertical,
  Star,
  Clock,
  Image,
  Video,
  Music,
  Archive,
  Code,
  Files,
  Info
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { DocumentResponse } from '../types/api';
import { API_BASE_URL } from '../services/api';

// Types pour la navigation
type NavigationPath = {
  type: 'root' | 'year' | 'level' | 'subject';
  value: string;
  label: string;
};

type FolderItem = {
  id: string;
  name: string;
  type: 'folder' | 'file';
  count?: number;
  document?: DocumentResponse;
  icon: React.ReactNode;
  color: string;
  size?: string;
  modified?: string;
};

export default function Documents() {
  const { documents, isLoadingDocuments, refreshDocuments } = useData();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24); // responsive: 12 mobile, 24 desktop (ajustable)
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState<number>(24);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState<'explorer' | 'list'>('explorer');
  const [navigationPath, setNavigationPath] = useState<NavigationPath[]>([
    { type: 'root', value: '', label: 'Documents' }
  ]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedNameId, setExpandedNameId] = useState<string | null>(null);
  const toggleExpandedName = (id: string) => {
    setExpandedNameId(prev => (prev === id ? null : id));
  };

  // Debug navigation
  useEffect(() => {
    console.log('=== NAVIGATION DEBUG ===');
    console.log('Navigation path:', navigationPath);
    console.log('Documents total:', documents.length);
  }, [navigationPath, documents]);

  // Calculer les statistiques
  const stats = useMemo(() => {
    const totalDocuments = documents.length;
    const uniqueYears = new Set(documents.map(doc => doc.year).filter(Boolean) as (number | string)[]);
    const uniqueLevels = new Set(documents.map(doc => doc.level).filter(Boolean));
    const uniqueSubjects = new Set(documents.map(doc => doc.subject).filter(Boolean));
    
    return {
      totalDocuments,
      totalYears: uniqueYears.size,
      totalLevels: uniqueLevels.size,
      totalSubjects: uniqueSubjects.size
    };
  }, [documents]);

  // Extraire les années des documents
  const availableYears = useMemo(() => {
    const years = documents
      .map(doc => (doc.year ? String(doc.year) : ''))
      .filter(y => y);
    return Array.from(new Set(years)).sort((a, b) => b.localeCompare(a));
  }, [documents]);

  // Extraire les matières uniques
  const availableSubjects = useMemo(() => {
    return Array.from(new Set(documents.map(doc => doc.subject))).sort();
  }, [documents]);

  // Extraire les niveaux uniques
  const availableLevels = useMemo(() => {
    return Array.from(new Set(documents.map(doc => doc.level))).sort();
  }, [documents]);

  // Obtenir la couleur selon la catégorie
  const getCategoryColor = (category: string | undefined): string => {
    switch ((category || '').toUpperCase()) {
      case 'OFFICIAL': return 'text-blue-700';
      case 'TD': return 'text-purple-700';
      case 'CORRECTION': return 'text-emerald-700';
      default: return 'text-gray-700';
    }
  };

  // Obtenir l'icône selon le type de document
  const getFileIcon = (document: DocumentResponse) => {
    const category = (document.category || '').toUpperCase();
    const type = (document.type || '').toUpperCase();
    
    if (type === 'PDF' || category === 'OFFICIAL') {
      return <FileText className="h-10 w-10 text-red-600" />;
    }
    if (type === 'IMAGE' || category === 'IMAGE') {
      return <Image className="h-10 w-10 text-red-600" />;
    }
    if (type === 'VIDEO') {
      return <Video className="h-10 w-10 text-red-600" />;
    }
    if (type === 'AUDIO') {
      return <Music className="h-10 w-10 text-red-600" />;
    }
    if (type === 'CODE') {
      return <Code className="h-10 w-10 text-red-600" />;
    }
    if (type === 'ARCHIVE') {
      return <Archive className="h-10 w-10 text-red-600" />;
    }
    return <FileText className="h-10 w-10 text-red-600" />;
  };

  // Documents filtrés par recherche et niveau uniquement (pas par navigation)
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesSearch = searchTerm === '' || 
        (doc.title && doc.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.subject && doc.subject.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesLevel = selectedLevel === '' || doc.level === selectedLevel;
      const matchesCategory = selectedCategory === '' || doc.category === selectedCategory;
    
      return matchesSearch && matchesLevel && matchesCategory;
    });
  }, [documents, searchTerm, selectedLevel, selectedCategory]);

  // Pagination côté client
  const totalItems = filteredDocuments.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const pagedDocuments = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return filteredDocuments.slice(start, start + pageSize);
  }, [filteredDocuments, currentPageSafe, pageSize]);

  useEffect(() => {
    // Réinitialiser à la première page quand les filtres changent
    setCurrentPage(1);
  }, [searchTerm, selectedLevel, selectedCategory]);

  // Détection mobile + initialisation du visibleCount
  useEffect(() => {
    const mm = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mm.matches);
    update();
    mm.addEventListener('change', update);
    return () => mm.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    // Reset du compteur visible sur changement de paramètres
    setMobileVisibleCount(Math.max(12, pageSize));
  }, [pageSize, searchTerm, selectedLevel, selectedCategory, navigationPath, viewMode]);

  // IntersectionObserver pour infinite scroll (mobile + vue liste)
  useEffect(() => {
    if (!isMobile || viewMode !== 'list') return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setMobileVisibleCount((prev) => Math.min(prev + pageSize, filteredDocuments.length));
        }
      });
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, viewMode, pageSize, filteredDocuments.length]);

  // Détection mobile + initialisation du visibleCount
  useEffect(() => {
    const mm = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mm.matches);
    update();
    mm.addEventListener('change', update);
    return () => mm.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    // Reset du compteur visible sur changement de paramètres
    setMobileVisibleCount(Math.max(12, pageSize));
  }, [pageSize, searchTerm, selectedLevel, selectedCategory, navigationPath, viewMode]);

  // IntersectionObserver pour infinite scroll (mobile + vue liste)
  useEffect(() => {
    if (!isMobile || viewMode !== 'list') return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setMobileVisibleCount((prev) => Math.min(prev + pageSize, filteredDocuments.length));
        }
      });
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, viewMode, pageSize, filteredDocuments.length]);

  // Obtenir le contenu actuel selon le chemin de navigation
  const getCurrentContent = useMemo((): FolderItem[] => {
    const currentPath = navigationPath[navigationPath.length - 1];
    
    console.log('Current path:', currentPath);
    console.log('Filtered documents:', filteredDocuments.length);
    
    if (currentPath.type === 'root') {
      // Afficher les dossiers par année
      const source = viewMode === 'list' ? pagedDocuments : filteredDocuments;
      const yearGroups = source.reduce((acc, doc) => {
        const yearVal = doc.year ? String(doc.year) : '';
        if (!yearVal) return acc;
        if (!acc[yearVal]) acc[yearVal] = 0;
        acc[yearVal]++;
        return acc;
      }, {} as Record<string, number>);

      console.log('Year groups:', yearGroups);

      return Object.entries(yearGroups)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([year, count]) => ({
          id: `year-${year}`,
          name: year,
          type: 'folder' as const,
          count,
          icon: <Folder className="h-16 w-16" />,
          color: 'text-blue-600',
          modified: new Date().toLocaleDateString()
        }));
    }
    
    if (currentPath.type === 'year') {
      // Afficher les niveaux pour cette année
      const selectedYear = currentPath.value;
      const source = viewMode === 'list' ? pagedDocuments : filteredDocuments;
      const yearDocs = source.filter(doc => String(doc.year ?? '') === selectedYear);
      
      console.log(`Documents for year ${selectedYear}:`, yearDocs.length);
      
      const levelGroups = yearDocs.reduce((acc, doc) => {
        const level = doc.level;
        // Inclure tous les niveaux y compris "Tous niveaux"
        if (level && level.trim() !== '') {
          if (!acc[level]) acc[level] = 0;
          acc[level]++;
        }
        return acc;
      }, {} as Record<string, number>);

      console.log(`Level groups for ${selectedYear}:`, levelGroups);

      // Ordre logique des niveaux scolaires 
      const levelOrder = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale', 'Tous niveaux'];
      
      return Object.entries(levelGroups)
        .sort(([a], [b]) => {
          const indexA = levelOrder.indexOf(a);
          const indexB = levelOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        .map(([level, count]) => ({
          id: `level-${level}`,
          name: level,
          type: 'folder' as const,
          count,
          icon: <GraduationCap className="h-16 w-16" />,
          color: 'text-purple-600',
          modified: new Date().toLocaleDateString()
        }));
    }
    
    if (currentPath.type === 'level') {
      // Afficher les matières pour ce niveau dans l'année sélectionnée
      const selectedYear = navigationPath.find(p => p.type === 'year')?.value;
      const selectedLevel = currentPath.value;
      
      const source = viewMode === 'list' ? pagedDocuments : filteredDocuments;
      const levelDocs = source.filter(doc => {
        const docYear = String(doc.year ?? '');
        const docLevel = doc.level;
        console.log(`Filtering doc: ${doc.title}, Year: ${docYear}, Level: ${docLevel}, Target Year: ${selectedYear}, Target Level: ${selectedLevel}`);
        return docYear === selectedYear && docLevel === selectedLevel;
      });
      
      console.log(`Documents for level ${selectedLevel} in year ${selectedYear}:`, levelDocs.length);
      
      const subjectGroups = levelDocs.reduce((acc, doc) => {
        const subject = doc.subject;
        if (subject && subject.trim() !== '') {
          if (!acc[subject]) acc[subject] = 0;
          acc[subject]++;
        }
        return acc;
      }, {} as Record<string, number>);

      console.log(`Subject groups for ${selectedLevel}:`, subjectGroups);

      const result = Object.entries(subjectGroups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([subject, count]) => ({
          id: `subject-${subject}`,
          name: subject,
          type: 'folder' as const,
          count,
          icon: <Book className="h-16 w-16" />,
          color: 'text-orange-600',
          modified: new Date().toLocaleDateString()
        }));
      
      console.log(`Returning ${result.length} subjects for level ${selectedLevel}:`, result.map(r => r.name));
      return result;
    }
    
    if (currentPath.type === 'subject') {
      // Afficher les documents pour cette matière dans le niveau et l'année sélectionnés
      const selectedYear = navigationPath.find(p => p.type === 'year')?.value;
      const selectedLevel = navigationPath.find(p => p.type === 'level')?.value;
      const selectedSubject = currentPath.value;
      
      console.log('Navigating to subject:', selectedSubject, 'in level:', selectedLevel, 'year:', selectedYear);
      console.log('Navigation path before filtering:', navigationPath);
      
      const source = viewMode === 'list' ? pagedDocuments : filteredDocuments;
      const subjectDocs = source.filter(doc => {
        const docYear = String(doc.year ?? '');
        const docLevel = doc.level;
        const docSubject = doc.subject;
        const matches = docYear === selectedYear && docLevel === selectedLevel && docSubject === selectedSubject;
        console.log(`Doc: ${doc.title}, Year: ${docYear}, Level: ${docLevel}, Subject: ${docSubject}, Matches: ${matches}`);
        return matches;
      });
      
      console.log(`Final documents for ${selectedSubject} in ${selectedLevel} (${selectedYear}):`, subjectDocs.length);
      
      return subjectDocs.map(doc => ({
        id: `doc-${doc.id}`,
        name: doc.title,
        type: 'file' as const,
        size: '2.5 MB', // TODO: calculer la vraie taille
        modified: doc.year ? String(doc.year) : '-',
        icon: getFileIcon(doc),
        color: getCategoryColor(doc.category),
        document: doc
      }));
    }
    
    return [];
  }, [navigationPath, filteredDocuments]);

  // Navigation
  const navigateToFolder = (item: FolderItem) => {
    console.log('Navigating to folder:', item);
    
    if (item.type === 'folder') {
      if (item.id.startsWith('year-')) {
        const year = item.id.replace('year-', '');
        console.log('Navigating to year:', year);
        const newPath = [
          ...navigationPath,
          { type: 'year' as const, value: year, label: year }
        ];
        console.log('New navigation path:', newPath);
        setNavigationPath(newPath);
      } else if (item.id.startsWith('level-')) {
        const level = item.id.replace('level-', '');
        console.log('Navigating to level:', level);
        const newPath = [
          ...navigationPath,
          { type: 'level' as const, value: level, label: level }
        ];
        console.log('New navigation path:', newPath);
        setNavigationPath(newPath);
      } else if (item.id.startsWith('subject-')) {
        const subject = item.id.replace('subject-', '');
        console.log('Navigating to subject:', subject);
        const newPath = [
          ...navigationPath,
          { type: 'subject' as const, value: subject, label: subject }
        ];
        console.log('New navigation path:', newPath);
        setNavigationPath(newPath);
      }
    }
  };

  const navigateToPath = (index: number) => {
    console.log('Navigating to path index:', index);
    const newPath = navigationPath.slice(0, index + 1);
    console.log('New path:', newPath);
    setNavigationPath(newPath);
  };

  const goBack = () => {
    if (navigationPath.length > 1) {
      console.log('Going back');
      const newPath = navigationPath.slice(0, -1);
      console.log('New path after going back:', newPath);
      setNavigationPath(newPath);
    }
  };

  // Actions sur les documents
  const handlePreview = (document: DocumentResponse) => {
    // Utiliser l'API backend pour la prévisualisation
    const previewUrl = `${API_BASE_URL}/documents/download/${document.id}?preview=true`;
    
    // Sur mobile, utiliser location.href pour éviter les problèmes de popup
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = previewUrl;
    } else {
      window.open(previewUrl, '_blank');
    }
  };

  const handleDownload = (doc: DocumentResponse) => {
    const downloadUrl = `${API_BASE_URL}/documents/download/${doc.id}?preview=false`;
    window.location.href = downloadUrl;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLevel('');
    setSelectedSubject('');
    setSelectedYear('');
    setSelectedCategory('');
  };

  const hasActiveFilters = searchTerm || selectedLevel || selectedSubject || selectedYear || selectedCategory;

  if (isLoadingDocuments) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-6 text-xl font-medium text-gray-900">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* En-tête clair et spacieux */}
        <div className="mb-4 sm:mb-6 md:mb-10">
          <div className="text-center md:text-left">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 md:mb-3">Documents de l'École</h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">Consultez et téléchargez les documents pédagogiques partagés par l'établissement</p>
          </div>
        </div>

        {/* Statistiques avec contraste élevé */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-8 mb-4 sm:mb-6 md:mb-10">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg md:rounded-xl p-3 sm:p-4 md:p-8">
              <div className="flex items-center">
              <div className="bg-blue-600 rounded-lg p-2 md:p-4">
                <Files className="h-6 w-6 md:h-8 md:w-8 text-white" />
                </div>
              <div className="ml-3 md:ml-6">
                <p className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">{stats.totalDocuments}</p>
                <p className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">Documents</p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg md:rounded-xl p-3 sm:p-4 md:p-8">
              <div className="flex items-center">
              <div className="bg-purple-600 rounded-lg p-2 md:p-4">
                <Calendar className="h-6 w-6 md:h-8 md:w-8 text-white" />
                </div>
              <div className="ml-3 md:ml-6">
                <p className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">{stats.totalYears}</p>
                <p className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">Années</p>
              </div>
            </div>
          </div>
          
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg md:rounded-xl p-3 sm:p-4 md:p-8">
              <div className="flex items-center">
              <div className="bg-emerald-600 rounded-lg p-2 md:p-4">
                <BookOpen className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <div className="ml-3 md:ml-6">
                <p className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">{stats.totalSubjects}</p>
                <p className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">Matières</p>
              </div>
            </div>
          </div>
        </div>

        {/* Zone principale avec bordures claires */}
        <div className="bg-white border-2 border-gray-300 rounded-lg md:rounded-xl shadow-lg overflow-hidden">
          {/* Barre d'outils avec séparation claire */}
          <div className="bg-gray-100 border-b-2 border-gray-300 px-3 sm:px-4 md:px-8 py-3 sm:py-4 md:py-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4 md:gap-6">
                            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 overflow-x-auto">
                {navigationPath.length > 1 && (
                  <button
                    onClick={goBack}
                    className="p-2 md:p-3 hover:bg-gray-200 rounded-lg transition-colors border-2 border-gray-300 flex-shrink-0"
                  >
                    <ArrowLeft className="h-4 w-4 md:h-6 md:w-6 text-gray-700" />
                  </button>
                )}
                
                {/* Breadcrumb avec séparateurs clairs */}
                <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3 text-sm md:text-lg whitespace-nowrap">
                  {navigationPath.map((path, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <ChevronRight className="h-4 w-4 md:h-6 md:w-6 text-gray-500 flex-shrink-0" />
                      )}
            <button
                        onClick={() => navigateToPath(index)}
                        className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg transition-colors font-medium flex-shrink-0 ${
                          index === navigationPath.length - 1
                            ? 'text-gray-900 bg-white border-2 border-gray-300 font-bold'
                            : 'text-blue-700 hover:text-blue-900 hover:bg-blue-50 border-2 border-transparent hover:border-blue-200'
                        }`}
                      >
                        {index === 0 ? (
                          <div className="flex items-center space-x-1 md:space-x-2">
                            <Home className="h-4 w-4 md:h-5 md:w-5" />
                            <span className="hidden sm:inline">Documents</span>
                          </div>
                        ) : (
                          <span className="truncate max-w-24 md:max-w-none">{path.label}</span>
                        )}
            </button>
                    </React.Fragment>
                  ))}
                </div>
          </div>

                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                {/* Recherche responsive */}
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4 sm:h-5 sm:w-5" />
                  <input
                    type="text"
                    placeholder="Rechercher un document..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 sm:pl-10 pr-3 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-sm sm:text-base md:text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64 md:w-80"
                  />
                </div>

                {/* Filtres responsive */}
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
                  className="border-2 border-gray-300 rounded-lg px-3 py-2 md:px-4 md:py-3 text-sm sm:text-base md:text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
            >
                  <option value="">Tous niveaux</option>
                  {availableLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>

                {hasActiveFilters && (
            <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 text-red-700 hover:text-red-900 px-3 py-2 md:px-4 md:py-3 rounded-lg hover:bg-red-50 text-sm sm:text-base md:text-lg font-medium border-2 border-red-200 hover:border-red-300"
            >
                    <X className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="hidden sm:inline">Effacer</span>
            </button>
          )}

                {/* Sélecteur de vue responsive */}
                <div className="flex bg-white border-2 border-gray-300 rounded-lg p-1">
              <button
                    onClick={() => setViewMode('explorer')}
                    className={`p-2 md:p-3 rounded-md transition-colors ${
                      viewMode === 'explorer' 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title="Vue icônes"
                  >
                    <Grid className="h-5 w-5 md:h-6 md:w-6" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                    className={`p-2 md:p-3 rounded-md transition-colors ${
                  viewMode === 'list' 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Vue liste"
              >
                    <List className="h-5 w-5 md:h-6 md:w-6" />
              </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contenu principal responsive */}
          <div className="p-3 sm:p-4 md:p-8">
            {getCurrentContent.length > 0 ? (
              viewMode === 'explorer' ? (
                /* Vue explorateur responsive */
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-8">
                  {getCurrentContent.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="group cursor-pointer p-4 md:p-6 rounded-xl hover:bg-gray-50 transition-colors border-2 border-transparent hover:border-gray-300"
                      onClick={() => item.type === 'folder' ? navigateToFolder(item) : undefined}
                      onDoubleClick={() => item.type === 'file' && item.document ? handlePreview(item.document) : undefined}
                    >
                      <div className="text-center">
                        <div className={`
                          inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300
                          ${item.type === 'folder' ? 'bg-blue-100' : 'bg-gray-100'}
                        `}>
                          {item.icon}
                        </div>
                        
                        <div className="flex items-center justify-center gap-2 mb-1 md:mb-2">
                          <h3 className="text-sm md:text-base font-semibold text-gray-900 truncate" title={item.name}>
                            {item.name}
                          </h3>
                          {item.type === 'file' && (
                            <div className="relative group">
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpandedName(item.id); }}
                                className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Afficher le titre complet"
                              >
                                <Plus className="h-4 w-4 text-gray-700" />
                              </button>
                              <div className="hidden md:block absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none">
                                <div className="bg-gray-900 text-white text-xs rounded-md px-2 py-1 whitespace-normal max-w-xs">
                                  {item.name}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {item.type === 'file' && expandedNameId === item.id && (
                          <p className="text-xs text-gray-800 break-words mb-2">{item.name}</p>
                        )}
                        
                        {item.type === 'file' && item.document && (
                          <>
                            <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">
                              {item.size} • {item.modified}
                            </p>
                            <span className="inline-block text-[10px] md:text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded mb-2 md:mb-3">
                              {item.document.category || '-'}
                            </span>
                            
                            <div className="flex flex-col space-y-1 md:space-y-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreview(item.document!);
                                }}
                                className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs md:text-sm font-medium py-1 md:py-2 px-2 md:px-3 rounded-md transition-colors flex items-center justify-center space-x-1"
                              >
                                <Eye className="h-3 w-3 md:h-4 md:w-4" />
                                <span>Voir</span>
                              </button>
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(item.document!);
                                }}
                                className="w-full bg-green-500 hover:bg-green-600 text-white text-xs md:text-sm font-medium py-1 md:py-2 px-2 md:px-3 rounded-md transition-colors flex items-center justify-center space-x-1"
                              >
                                <Download className="h-3 w-3 md:h-4 md:w-4" />
                                <span>Télécharger</span>
                              </button>
                            </div>
                          </>
                        )}
                        
                        {item.type === 'folder' && item.count !== undefined && (
                          <p className="text-xs md:text-sm text-gray-500">
                            {item.count} élément{item.count !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                /* Vue liste macOS style */
                <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                  {/* En-tête de la liste */}
                  <div className="bg-gray-50 border-b-2 border-gray-200 px-4 md:px-6 py-3 md:py-4 hidden sm:grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                    <div className="col-span-5">Nom</div>
                    <div className="col-span-2">Catégorie</div>
                    <div className="col-span-2">Date de modification</div>
                    <div className="col-span-2">Taille</div>
                    <div className="col-span-1">Actions</div>
                  </div>
                  
                  {/* Liste des éléments */}
                  <div className="divide-y divide-gray-200">
                    {(() => {
                      const currentPath = navigationPath[navigationPath.length - 1];
                      const isSubject = currentPath.type === 'subject';
                      let items = getCurrentContent;
                      if (isSubject) {
                        if (isMobile) {
                          items = items.slice(0, mobileVisibleCount);
                        } else {
                          const start = (currentPage - 1) * pageSize;
                          items = items.slice(start, start + pageSize);
                        }
                      }
                      return items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        className={`group px-4 md:px-6 py-3 md:py-4 hover:bg-blue-50 transition-colors cursor-pointer ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                        onClick={() => item.type === 'folder' ? navigateToFolder(item) : undefined}
                        onDoubleClick={() => item.type === 'file' && item.document ? handlePreview(item.document) : undefined}
                      >
                        {/* Version desktop */}
                        <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-5 flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${item.type === 'folder' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              {item.icon}
                            </div>
                            <div className="relative group/tooltip max-w-full">
                              <span className="font-medium text-gray-900 truncate inline-block align-middle max-w-[14rem] md:max-w-[22rem]">{item.name}</span>
                              {/* Tooltip desktop */}
                              <div className="hidden md:block pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute z-50 left-0 top-full mt-2 w-max max-w-xl">
                                <div className="bg-gray-900 text-white text-sm rounded-lg shadow-2xl p-3 border border-white/10">
                                  <div className="flex items-start space-x-2">
                                    <Info className="h-4 w-4 text-blue-400 mt-0.5" />
                                    <div className="space-y-1">
                                      <div className="font-semibold">Titre complet</div>
                                      <div className="text-gray-200 whitespace-normal break-words">{item.name}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Bouton mobile pour ouvrir une feuille/overlay */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpandedName(item.id); }}
                                className="md:hidden ml-2 text-xs text-blue-600 underline"
                              >Voir</button>
                              <AnimatePresence>
                                {expandedNameId === item.id && (
                                  <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="fixed inset-0 z-50 md:hidden"
                                    onClick={() => toggleExpandedName(item.id)}
                                  >
                                    <div className="absolute inset-0 bg-black/50" />
                                    <motion.div
                                      initial={{ y: '100%' }}
                                      animate={{ y: 0 }}
                                      exit={{ y: '100%' }}
                                      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                                      className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 shadow-2xl"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                          <Info className="h-5 w-5 text-blue-600" />
                                          <h4 className="font-semibold text-gray-900">Titre complet</h4>
                                        </div>
                                        <button onClick={() => toggleExpandedName(item.id)} className="text-sm text-gray-600">Fermer</button>
                                      </div>
                                      <p className="text-sm text-gray-800 break-words">{item.name}</p>
                                    </motion.div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className="col-span-2 text-sm text-gray-700">
                            {item.type === 'file' && item.document ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                                {item.document.category || '-'}
                              </span>
                            ) : '-'}
                          </div>
                          <div className="col-span-2 text-sm text-gray-600">
                            {item.modified || '-'}
                          </div>
                          <div className="col-span-2 text-sm text-gray-600">
                            {item.size || '-'}
                          </div>
                          <div className="col-span-1 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {item.type === 'file' && item.document && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreview(item.document!);
                                  }}
                                  className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                                  title="Voir"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(item.document!);
                                  }}
                                  className="p-2 text-green-600 hover:bg-green-100 rounded-md transition-colors"
                                  title="Télécharger"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Version mobile */}
                        <div className="sm:hidden">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className={`p-2 rounded-lg ${item.type === 'folder' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900 truncate" title={item.name}>{item.name}</h3>
                                {item.type === 'file' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleExpandedName(item.id); }}
                                    className="p-1 rounded-md bg-gray-100 hover:bg-gray-200"
                                    aria-label="Afficher le titre complet"
                                  >
                                    <Plus className="h-4 w-4 text-gray-700" />
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {item.modified} • {item.size}
                              </p>
                              {item.type === 'file' && item.document && (
                                <span className="inline-block mt-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                                  {item.document.category || '-'}
                                </span>
                              )}
                              {item.type === 'file' && expandedNameId === item.id && (
                                <p className="text-xs text-gray-800 break-words mt-1">{item.name}</p>
                              )}
                            </div>
                          </div>
                          
                          {item.type === 'file' && item.document && (
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreview(item.document!);
                                }}
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors flex items-center justify-center space-x-1"
                              >
                                <Eye className="h-4 w-4" />
                                <span>Voir</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(item.document!);
                                }}
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors flex items-center justify-center space-x-1"
                              >
                                <Download className="h-4 w-4" />
                                <span>Télécharger</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                      ));
                    })()}
                  </div>
                  {/* Sentinel pour infinite scroll (mobile + liste + subject) */}
                  {(() => {
                    const currentPath = navigationPath[navigationPath.length - 1];
                    const isSubject = currentPath.type === 'subject';
                    return isMobile && isSubject ? <div ref={loadMoreRef} className="h-8" /> : null;
                  })()}
                </div>
              )
            ) : (
              <div className="text-center py-12 md:py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-100 mb-4 md:mb-6">
                  <FileText className="h-8 w-8 md:h-10 md:w-10 text-gray-400" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">Aucun document trouvé</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {hasActiveFilters 
                    ? 'Aucun document ne correspond à vos critères de recherche. Essayez de modifier vos filtres.'
                    : 'Aucun document disponible pour le moment.'
                  }
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <X className="h-4 w-4" />
                    Effacer tous les filtres
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pagination controls (desktop/tablette + subject uniquement) */}
          {(() => {
            const currentPath = navigationPath[navigationPath.length - 1];
            const isSubject = currentPath.type === 'subject';
            if (!isSubject) return null;
            const totalPagesLocal = Math.max(1, Math.ceil(getCurrentContent.length / pageSize));
            const currentPageLocal = Math.min(currentPage, totalPagesLocal);
            return (
              <div className="mt-6 hidden sm:flex items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Page {currentPageLocal} / {totalPagesLocal} • {getCurrentContent.length} éléments
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPageLocal === 1}
                    className="px-3 py-2 rounded-md border text-sm disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPagesLocal, p + 1))}
                    disabled={currentPageLocal === totalPagesLocal}
                    className="px-3 py-2 rounded-md border text-sm disabled:opacity-50"
                  >
                    Suivant
                  </button>
                  <select
                    value={pageSize}
                    onChange={e => setPageSize(parseInt(e.target.value, 10))}
                    className="ml-2 border rounded-md px-2 py-2 text-sm"
                  >
                    <option value={12}>12 / page</option>
                    <option value={24}>24 / page</option>
                    <option value={48}>48 / page</option>
                  </select>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}